import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import queryString from "query-string";
import { db } from "../firebase";
import {
  doc,
  updateDoc,
  increment,
  collection,
  getDocs,
} from "firebase/firestore";
import Cache from "../utils/cache";

import ProductTile from "./ProductTile";
import BankOfferTile from "./BankOfferTile";
import RebateModel from "./RebateModal";
import CashbackModal from "./CashbackModal";
import BankOfferModel from "./BankOfferModal";
import { matchBankOffersToProducts } from "../utils/vendorMatchUtils";


export default function OfferList() {
  const location = useLocation();

  const { search: searchTermRaw } = queryString.parse(location.search);
  const searchTerm = decodeURIComponent(searchTermRaw || "");
  const navigate = useNavigate();

  const [productOffers, setProductOffers] = useState([]);
  const [bankOffers, setBankOffers] = useState([]);
  const [matchedBankOfferMap, setMatchedBankOfferMap] = useState({});
  const [selectedBankOffer, setSelectedBankOffer] = useState(null);
  const [banksLogos, setBanksLogos] = useState({});
  const [banksAddresses, setBanksAddresses] = useState({});
  const [sortOption, setSortOption] = useState(() => {
    const params = queryString.parse(window.location.search);
    return typeof params.sort === "string" ? params.sort : "newest";
  });
  const [loading, setLoading] = useState(false);

  const [showRebatePopup, setShowRebatePopup] = useState(false);
  const [selectedRebateOffer, setSelectedRebateOffer] = useState(null);
  const [showCashbackPopup, setShowCashbackPopup] = useState(false);
  const [selectedCashbackOffer, setSelectedCashbackOffer] = useState(null);
  const prevSortOrderRef = useRef(null);


  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const containsWine = useCallback((text) => text?.toLowerCase().includes("wine"), []);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const initialPageSetRef = useRef(false);

  useEffect(() => {
    if (initialPageSetRef.current) return;

    const params = queryString.parse(location.search);
    const pageFromUrl = parseInt(params.page || "1", 10);
    if (!isNaN(pageFromUrl)) {
      setCurrentPage(pageFromUrl);
    }

    const sortFromUrl = typeof params.sort === "string" ? params.sort : "newest";

    if (!isNaN(pageFromUrl) && pageFromUrl > 0) {
      setCurrentPage(pageFromUrl);
    }

    // ✅ Sync both sortOrder and prevSortOrderRef correctly
    setSortOption(sortFromUrl);
    prevSortOrderRef.current = sortFromUrl;

    initialPageSetRef.current = true;
  }, [location.search]); // ✅ include location.search now



  // Update URL when page or sortOrder changes
  useEffect(() => {
    const params = queryString.parse(location.search);
    let needUpdate = false;

    if (parseInt(params.page || "1", 10) !== currentPage) {
      params.page = currentPage;
      needUpdate = true;
    }
    if ((params.sort || "newest") !== sortOption) {
      params.sort = sortOption;
      needUpdate = true;
    }

    if (needUpdate) {
      const newSearch = queryString.stringify(params);
      navigate({ search: newSearch }, { replace: true });
    }
  }, [currentPage, navigate, location.search, sortOption]);

  const prevSearchRef = useRef(searchTerm);
  useEffect(() => {
    if (prevSearchRef.current !== searchTerm) {
      setCurrentPage(1);
      prevSearchRef.current = searchTerm;
    }
  }, [searchTerm]);


  const loadCacheFromDb = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      // UTC start of today for date comparison:
      const nowUTC = new Date();
      const fiveDaysLater = new Date();
      fiveDaysLater.setDate(nowUTC.getDate() + 5);

      // Load product offers from new structure (array of offers per doc)
      const productSnap = await getDocs(collection(db, "product_offers"));
      let allOffers = [];
      productSnap.forEach((doc) => {
        const data = doc.data();
        if (Array.isArray(data.offers)) {
          allOffers = allOffers.concat(data.offers.map(offer => ({
            ...offer,
            // add doc id + index for unique id to track likes/votes etc
            id: `${doc.id}_${offer.offerId || Math.random().toString(36).substring(2, 8)}`,
            uploadedDttm: offer.uploadedDttm || data.uploadedDttm || null,
          })));
        }
      });

      const productList = allOffers.filter((offer) => {
        // Convert Firestore Timestamp to JS Date, fallback to Date if needed
        const endDate = offer.endDttm?.toDate ? offer.endDttm.toDate() : offer.endDttm ? new Date(offer.endDttm) : null;
        const startDate = offer.startDttm?.toDate ? offer.startDttm.toDate() : offer.startDttm ? new Date(offer.startDttm) : null;

        return (
          (!endDate || endDate >= nowUTC) &&  // not expired
          (!startDate || startDate <= fiveDaysLater) &&  // already started
          !containsWine(offer.vendor) &&
          !containsWine(offer.description)
        );
      });

      // The rest of your bankOffers and banksMeta loading unchanged...

      // (Copy bankOffers loading code here unchanged)
      const bankOffersSnap = await getDocs(collection(db, "bank_offers"));
      let bankList = [];
      bankOffersSnap.forEach((doc) => {
        const bankName = doc.id;
        const data = doc.data();
        if (Array.isArray(data.offers)) {
          const offersWithBank = data.offers
            .filter(
              (offer) =>
                (!offer.expiryDttm || (offer.expiryDttm.toDate ? offer.expiryDttm.toDate() > now : new Date(offer.expiryDttm) > now)) &&
                !containsWine(offer.vendor) &&
                !containsWine(offer.offer)
            )
            .map((offer, idx) => ({
              id: `${bankName}_offer_${idx}`,
              bankName,
              ...offer,
            }));
          bankList = bankList.concat(offersWithBank);
        }
      });

      const banksSnap = await getDocs(collection(db, "banks"));
      const banksMeta = {};
      banksSnap.forEach((doc) => {
        const bankName = doc.id.toLowerCase();
        const data = doc.data();
        banksMeta[bankName] = {
          logo: data.logo || "",
          address: data.address || "",
        };
      });

      Cache.setData({
        productOffers: productList,
        bankOffers: bankList,
        banksMeta,
      });

      setProductOffers(productList);
      setBankOffers(bankList);

      const banksLogoMap = [];
      const banksAddrMap = [];
      Object.entries(banksMeta).forEach(([key, data]) => {
        banksAddrMap[key] = data.address + "";
        banksLogoMap[key] = data.logo + "";
      });

      setBanksLogos(banksLogoMap);
      setBanksAddresses(banksAddrMap);

      // Match bank offers to product offers by normalized vendor names (unchanged)
      const bankMap = matchBankOffersToProducts(productList, bankList);
      setMatchedBankOfferMap(bankMap);
      setLoading(false);
    } catch (error) {
      console.error("Error loading offers from DB:", error);
    } finally {
      setLoading(false);
    }
  }, [containsWine]);

  const normalize = useCallback((str) =>
    str
      ?.toLowerCase()
      .normalize("NFD")                      // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, "")      // Remove accents
      .replace(/['"`\-\s]/g, "")               // ✅ No escape needed for hyphen at end
      .replace(/[^a-z0-9\s]/g, "")          // Remove other special characters
      .replace(/\s+/g, " ")                 // Collapse multiple spaces
      .trim() || ""
    , []);


  const sortFunctions = useMemo(() => {
    const getDate = (o) =>
      o.uploadedDttm?.toDate ? o.uploadedDttm.toDate() : new Date(0);

    const getPrice = (o) => {
      const price = parseFloat(o.offerPrice);
      return isNaN(price) ? Infinity : price;
    };

    const getDiscount = (o) => {
      const offerPrice = parseFloat(o.offerPrice);
      const originalPrice = parseFloat(o.originalPrice);

      if (isNaN(offerPrice)) return null;

      // Free deal → treat as 100% discount
      if (offerPrice === 0) return 1;

      if (isNaN(originalPrice) || originalPrice <= 0) return null;

      return (originalPrice - offerPrice) / originalPrice;
    };

    return {
      newest: (a, b) => getDate(b) - getDate(a),
      oldest: (a, b) => getDate(a) - getDate(b),
      priceLow: (a, b) => getPrice(a) - getPrice(b),
      priceHigh: (a, b) => getPrice(b) - getPrice(a),
      bestDiscount: (a, b) => {
        const discA = getDiscount(a);
        const discB = getDiscount(b);

        // Case 1: both have discounts → sort by discount desc, tie → newest
        if (discA !== null && discB !== null) {
          if (discB !== discA) return discB - discA;
          return getDate(b) - getDate(a);
        }

        // Case 2: neither has discounts → sort by price asc, tie → newest
        if (discA === null && discB === null) {
          if (getPrice(a) !== getPrice(b)) return getPrice(a) - getPrice(b);
          return getDate(b) - getDate(a);
        }

        // Case 3: one has discount → discount first
        if (discA !== null && discB === null) return -1;
        if (discA === null && discB !== null) return 1;

        return 0;
      },
    };
  }, []);


  // Split searchTerm by comma into multiple keywords
  const filteredProducts = useMemo(() => {
    const keywords = searchTerm
      .split(",")
      .map((k) => normalize(k).trim())
      .filter((k) => k.length > 0);

    function normalizeForMatch(text) {
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // accents
        .replace(/[-_\s]+/g, "")         // remove hyphens, underscores, spaces
        .replace(/[^a-z0-9]/g, "");      // remove other special chars
    }

    // Helper: boundary-aware match for productName and description
    function matchField(field, keyword) {
      if (!field || !keyword) return false;

      const kw = normalizeForMatch(keyword);
      const normalizedField = normalizeForMatch(field);

      // short keywords: exact match in tokens
      if (keyword.length <= 3) {
        const tokens = field.toLowerCase().split(/\s+/);
        return tokens.some(token => normalizeForMatch(token) === kw);
      }

      // long keywords: match anywhere in normalized field
      return normalizedField.includes(kw);
    }

    return productOffers.filter((o) => {
      return keywords.every((kw) => {
        return (
          matchField(o.productName, kw) ||     // fixed
          matchField(o.description, kw) ||     // fixed
          normalize(o.offerType).includes(kw) ||
          normalize(o.vendor).includes(kw) ||
          normalize(o.category).includes(kw) ||
          o.tags?.some((tag) => normalize(tag).includes(kw)) ||
          matchedBankOfferMap[o.id]?.some((b) =>
            normalize(b.bankName).includes(kw)
          )
        );
      });
    });
  }, [productOffers, matchedBankOfferMap, normalize, searchTerm]);


  // Sort filtered product offers
  /* -- Old Sorting
  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort(sortFunctions[sortOption]);
  }, [filteredProducts, sortOption, sortFunctions]);
  */

  const sortedProducts = useMemo(() => {
    if (sortOption !== "newest") {
      return [...filteredProducts].sort(sortFunctions[sortOption]);
    }

    // 1. Group offers by vendor
    const offersByVendor = {};
    filteredProducts.forEach((offer) => {
      const vendor = offer.vendor || "unknown";
      if (!offersByVendor[vendor]) offersByVendor[vendor] = [];
      offersByVendor[vendor].push(offer);
    });

    // 2. Sort offers within each vendor by newest first
    for (const vendor in offersByVendor) {
      offersByVendor[vendor].sort((a, b) => {
        const dateA = a.uploadedDttm?.toDate ? a.uploadedDttm.toDate() : new Date(a.uploadedDttm);
        const dateB = b.uploadedDttm?.toDate ? b.uploadedDttm.toDate() : new Date(b.uploadedDttm);
        return dateB - dateA;
      });
    }

    // 3. Sort vendors descending by their latest uploaded offer datetime
    const vendorList = Object.keys(offersByVendor).sort((v1, v2) => {
      const offers1 = offersByVendor[v1];
      const offers2 = offersByVendor[v2];

      const latest1 = offers1.reduce((maxDate, o) => {
        const d = o.uploadedDttm?.toDate ? o.uploadedDttm.toDate() : new Date(o.uploadedDttm);
        return d > maxDate ? d : maxDate;
      }, new Date(0));

      const latest2 = offers2.reduce((maxDate, o) => {
        const d = o.uploadedDttm?.toDate ? o.uploadedDttm.toDate() : new Date(o.uploadedDttm);
        return d > maxDate ? d : maxDate;
      }, new Date(0));

      return latest2 - latest1; // descending order
    });

    // 4. Round robin one offer at a time per vendor
    const result = [];
    const pointers = Object.fromEntries(vendorList.map(v => [v, 0]));

    let added = true;
    while (added) {
      added = false;
      for (const vendor of vendorList) {
        const pointer = pointers[vendor];
        const offers = offersByVendor[vendor];
        if (pointer < offers.length) {
          result.push(offers[pointer]);
          pointers[vendor] = pointer + 1;
          added = true;
        }
      }
    }
    return result;
  }, [filteredProducts, sortOption, sortFunctions]);


  // Filter bank offers by searchTerm
  const filteredBankOffers = useMemo(() => {
    const keywords = searchTerm
      .split(",")
      .map(k => normalize(k).trim())
      .filter(k => k.length > 0);

    // Helper: boundary-aware match for vendor and offer
    function matchKeywordInField(field, keyword) {
      if (!field || !keyword) return false;

      const kw = normalize(keyword).toLowerCase().trim();
      const fieldNormalized = normalize(field).toLowerCase().trim();

      // Exact match first
      if (fieldNormalized === kw) return true;

      // Split tokens on spaces, hyphens, underscores (keep dots for domains)
      const tokens = fieldNormalized
        .replace(/[-_]+/g, " ")
        .split(/\s+/)
        .filter(Boolean);

      return tokens.some(token => {
        if (token === kw) return true;                // exact token
        if (token.startsWith(kw) || token.endsWith(kw)) return true; // prefix/suffix

        // Embedded subword: only if keyword length >= 5 to avoid false positives
        if (kw.length >= 5 && token.includes(kw)) return true;

        return false;
      });
    }

    return bankOffers.filter(b => {
      return keywords.every(kw => {
        return (
          matchKeywordInField(b.vendor, kw) ||  // vendor: boundary-aware
          matchKeywordInField(b.offer, kw) ||   // offer: boundary-aware
          // bankName: strict exact-token match
          (b.bankName &&
            b.bankName
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .split(/\s+/)
              .some(token => normalize(kw) === token))
        );
      });
    });
  }, [bankOffers, searchTerm, normalize]);

  // Combine product and bank offers into one sorted list with type tags
  const combinedSortedOffers = useMemo(() => {
    const productsWithType = sortedProducts.map((p) => ({ ...p, _type: "product" }));
    const banksWithType = filteredBankOffers.map((b) => ({ ...b, _type: "bank" }));

    const combined = [...productsWithType, ...banksWithType];

    return combined.sort((a, b) => {
      if (a._type === "product" && b._type === "product") {
        return 0;
      } else if (a._type === "bank" && b._type === "bank") {
        const aDate = a.expiryDttm ? (a.expiryDttm.toDate ? a.expiryDttm.toDate() : new Date(a.expiryDttm)) : new Date(0);
        const bDate = b.expiryDttm ? (b.expiryDttm.toDate ? b.expiryDttm.toDate() : new Date(b.expiryDttm)) : new Date(0);
        return bDate - aDate;
      } else {
        // Prioritize products first
        return a._type === "product" ? -1 : 1;
      }
    });
  }, [sortedProducts, filteredBankOffers]);

  // Pagination variables
  const totalPages = Math.ceil(combinedSortedOffers.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedOffers = combinedSortedOffers.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    if (currentPage < 1) {
      setCurrentPage(1);
    } else if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Vote handler for product offers
  const handleVote = useCallback(
    async (id, type) => {
      try {
        const offerRef = doc(db, "product_offers", id);
        if (type === "like") {
          await updateDoc(offerRef, { likes: increment(1) });
        } else if (type === "dislike") {
          await updateDoc(offerRef, { dislikes: increment(1) });
        }
        setProductOffers((prev) =>
          prev.map((o) =>
            o.id === id
              ? {
                ...o,
                likes: type === "like" ? (o.likes || 0) + 1 : o.likes || 0,
                dislikes: type === "dislike" ? (o.dislikes || 0) + 1 : o.dislikes || 0,
              }
              : o
          )
        );
      } catch (err) {
        console.error("Error updating vote:", err);
      }
    },
    [setProductOffers]
  );

  // Rebate modal handlers
  const handleRebateClick = useCallback((offer) => {
    setSelectedRebateOffer(offer);
    setShowRebatePopup(true);
  }, []);

  const closeRebatePopup = useCallback(() => {
    setShowRebatePopup(false);
    setSelectedRebateOffer(null);
  }, []);

  const handleCashbackClick = useCallback((offer) => {
    setSelectedCashbackOffer(offer);
    setShowCashbackPopup(true);
  }, []);

  const closeCashbackPopup = useCallback(() => {
    setShowCashbackPopup(false);
    setSelectedCashbackOffer(null);
  }, []);


  // Load cached data or fetch on mount
  useEffect(() => {
    const cached = Cache.getData();
    if (!cached || !cached.productOffers?.length || !cached.bankOffers?.length) {
      loadCacheFromDb().finally(() => setIsInitialLoad(false));
    } else {
      setLoading(true);
      const now = new Date();
      const nowUTC = new Date();
      const utcTodayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
      const fiveDaysLater = new Date();
      fiveDaysLater.setDate(nowUTC.getDate() + 5);

      const products = cached.productOffers.filter((doc) => {
        // Convert Firestore Timestamp or string to JS Date
        const endDate = doc.endDttm?.toDate ? doc.endDttm.toDate() : doc.endDttm ? new Date(doc.endDttm) : null;
        const startDate = doc.startDttm?.toDate ? doc.startDttm.toDate() : doc.startDttm ? new Date(doc.startDttm) : null;

        return (
          (!endDate || endDate >= nowUTC) &&         // not expired
          (!startDate || startDate <= fiveDaysLater) && // already started or starting within next 5 days
          !containsWine(doc.vendor) &&
          !containsWine(doc.description)
        );
      });

      const banks = cached.bankOffers.filter((doc) => {
        const expiryDate = doc.expiryDttm?.toDate ? doc.expiryDttm.toDate() : doc.expiryDttm ? new Date(doc.expiryDttm) : null;
        const utcExpiryDateStart = expiryDate
          ? new Date(Date.UTC(expiryDate.getUTCFullYear(), expiryDate.getUTCMonth(), expiryDate.getUTCDate()))
          : null;

        return (
          (!utcExpiryDateStart || utcExpiryDateStart > utcTodayStart) &&
          !containsWine(doc.vendor) &&
          !containsWine(doc.offer)
        );
      });

      const banksMeta = cached.banksMeta || {};
      const banksLogoMap = [];
      const banksAddrMap = [];
      Object.entries(banksMeta).forEach(([key, data]) => {
        banksAddrMap[key] = data.address + "";
        banksLogoMap[key] = data.logo + "";
      });

      setBanksLogos(banksLogoMap);

      const bankMap = matchBankOffersToProducts(products, banks);

      setProductOffers(products);
      setBankOffers(banks);
      setMatchedBankOfferMap(bankMap);
      setBanksAddresses(banksAddrMap);
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [loadCacheFromDb, containsWine]);

  const onSortChange = (newSort) => {
    if (newSort !== sortOption) {
      setSortOption(newSort);
    }
  };

  if (loading || isInitialLoad)
    return (
      <div className="flex justify-center items-center h-40">
        <svg
          className="animate-spin -ml-1 mr-3 h-10 w-10 text-indigo-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          ></path>
        </svg>
        <span className="text-indigo-600 font-semibold text-lg">Loading offers...</span>
      </div>
    );


  return (
    <div className="p-2">
      <>
        <style>{`
          @keyframes flashHighlight {
            0%, 100% { box-shadow: 0 0 10px 3px rgba(59, 130, 246, 0.6); }
            50% { box-shadow: 0 0 20px 8px rgba(59, 130, 246, 1); }
          }
        `}</style>

        {/* Sort dropdown */}
        <div className="mb-4 flex justify-end items-center gap-4">
          <label htmlFor="sortOrder" className="font-semibold">
            Sort By:
          </label>
          <select
            id="sortOrder"
            value={sortOption}
            onChange={(e) => onSortChange(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="priceLow">Price Low to High</option>
            <option value="priceHigh">Price High to Low</option>
            <option value="bestDiscount">Best Discount</option>
          </select>
        </div>

        {/* Offers grid */}
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 px-2 pb-8">
          {paginatedOffers.map((offer) => {
            if (offer._type === "product") {
              return (
                <ProductTile
                  key={offer.id}
                  offer={offer}
                  onClick={() => navigate(`/product/${offer.id}?page=${currentPage}&sort=${sortOption}`)}
                  onRebateClick={handleRebateClick}
                  onCashbackClick={handleCashbackClick}
                  likes={offer.likes || 0}
                  dislikes={offer.dislikes || 0}
                  onVote={handleVote}
                  matchedBankOffers={matchedBankOfferMap[offer.id]}
                  banksLogos={banksLogos}
                  setSelectedBankOffer={setSelectedBankOffer}
                />
              );
            } else {
              return (
                <BankOfferTile
                  key={offer.id}
                  offer={offer}
                  bankAddress={banksAddresses[offer.bankName?.toLowerCase()]}
                />
              );
            }
          })}
          {!loading && !isInitialLoad && combinedSortedOffers.length === 0 && (
            <p className="col-span-full text-center text-gray-500">No offers found.</p>
          )}
        </div>

        {/* Pagination controls */}
        <div className="flex justify-center gap-2 my-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((p) => (p < totalPages ? p + 1 : p))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>

        {/* Modals */}
        {selectedBankOffer && (
          <BankOfferModel
            offer={selectedBankOffer}
            banksLogos={banksLogos}
            banksAddresses={banksAddresses}
            onClose={() => setSelectedBankOffer(null)}
          />
        )}

        {showRebatePopup && selectedRebateOffer && (
          <RebateModel offer={selectedRebateOffer} onClose={closeRebatePopup} />
        )}

        {showCashbackPopup && selectedCashbackOffer && (
          <CashbackModal offer={selectedCashbackOffer} onClose={closeCashbackPopup} />
        )}
      </>
    </div>
  );
}
